import type { UUID } from './index';
import { ActivityModule } from './ActivityModule';
import { AvatarCompanionAPI } from './AvatarCompanion';
import type { ParticipantPuppet } from './ParticipantPuppet';
import type { RoomExperienceId } from './RoomExperience';
import SimplePeer from 'simple-peer';


export const ROOM_KINDS = [
    'standard',
    'talk_show',
    'board_room',
    'townhall',
    'concierge',
    'training',
] as const;

/** Valid room kind values */
export type RoomKind = typeof ROOM_KINDS[number];

/** @deprecated Use RoomKind. */
export type RoomType = RoomKind;

export interface Room {
    id: UUID;
    name: string;
    password: string | null;
    hostId: UUID | null;
    enabled?: boolean;
    is_private?: boolean;
    description?: string | null;
    title?: string | null;
    background_img_1?: string | null;
    background_img_2?: string | null;
    broadcast_url?: string | null;
    room_type?: RoomKind;
    /** What the room is for (#4888). Null on rooms not yet backfilled; see roomExperienceForRoom(). */
    experience?: RoomExperienceId | null;
    /** #4890: the share link token's version; Reset link increments it. */
    shareTokenVersion?: number;
    /**
     * IANA timezone the AI resolves dates/times against.
     * `null`/absent = no host has chosen: prompts fall back to UTC, CFB to Eastern.
     */
    timezone?: string | null;
};

/**
 * #4965 — whether a room is private. Rooms are read straight from the database, so the field is
 * `isPrivate`; `Room` declares `is_private`, and every check that read only that saw undefined —
 * which is how "private" never required sign-in and the Settings switch never saved.
 */
export function isRoomPrivate(room: { isPrivate?: boolean | null; is_private?: boolean | null } | null | undefined): boolean {
    return (room?.isPrivate ?? room?.is_private) === true;
}

export type RoomMetaData = {
    id: UUID;
    name: NonNullable<Room['name']>;
    title?: NonNullable<Room['title']>;
    description?: NonNullable<Room['description']>;
    is_private?: NonNullable<Room['is_private']>;
    enabled?: NonNullable<Room['enabled']>;
    room_type?: RoomKind;
    experience?: RoomExperienceId | null;
    broadcast_url?: string | null;
    timezone?: string | null;
    backgroundImage: {
        portrait?: NonNullable<Room['background_img_1']>;
        landscape?: NonNullable<Room['background_img_2']>;
    };
}


export enum RoomEvents {
  PARTICIPANTS_UPDATED = 'participants:updated',
  PARTICIPANT_JOINED = 'participant:joined',
  PARTICIPANT_LEFT = 'participant:left',
  PARTICIPANT_STREAM_UPDATED = 'participant:stream-updated', 
  ACTIVITY_MODULES_UPDATED = 'activity-modules:updated',
  STAGE_MODULE_ACTIVE = 'stage:module-active',
  STAGE_MODULE_INACTIVE = 'stage:module-inactive',
  MIC_MUTED = 'mic:muted',
  CAM_MUTED = 'cam:muted',
  SOCKET_FATAL_FAILURE = 'socket:fatal-failure',
  TRANSCRIPT_RECEIVED = 'transcript:received'
} 


export interface RoomContext {
  // Current participants
  participants?: RoomParticipantData[];

  // Active module state
  activeModule: ActivityModule | null;
}

export interface ActivityParticipant {
    sessionId: UUID  // used to identify a user; can be used
    alias: string // user name ; not necessarily their member name
    isHost: boolean  // whther the user is the host of the room
    roomId: string
    type?: ParticipantType; // Add optional type property
}
export const ParticipantType = {
    PARTICIPANT: 'participant',
    AGENT_TELE: 'agent-tele',
    PROGRAM_OBSERVER: 'program_observer'
} as const;

export type ParticipantType = typeof ParticipantType[keyof typeof ParticipantType];

export interface RoomParticipantData {
    alias: string;
    isHost: boolean;
    sessionId: UUID;
    mediaStream: MediaStream | null;
    roomId: string;
    id?: string;
    socketId?: string;
    type: ParticipantType;
    peerInstance?: SimplePeer.Instance | null;
    avatarCompanion?: AvatarCompanionAPI | null;
    /** #3837 — companion avatar this human participant is wearing; null for a video tile. */
    puppet?: ParticipantPuppet | null;
    isEnabled?: boolean; // Add isEnabled property
    isActive?: boolean;   // Add isActive property
}

// Event for setting the main view content
export const SET_MAIN_VIEW_CONTENT = 'set-main-view-content';

export type SetMainViewContentPayload =
  | { type: 'participant'; sessionId: UUID; } // Changed string to UUID
  | { type: 'activity'; moduleId: string; customData?: any; };
